import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CTABanner } from "@/components/site/CTABanner";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { PRINCIPLES } from "@/data/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1662019578129-2072e4dce5e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHx0dWNzb24lMjBtb3VudGFpbnxlbnwwfHx8fDE3OTA5ODg3NDJ8MA&ixlib=rb-4.1.0&q=85";
const NARRATIVE_IMG =
    "https://images.unsplash.com/photo-1549318558-02fe75fc51d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHxfc29ub3JhbiUyMGRlc2VydHxlbnwwfHx8fDE3OTA5ODg3NDJ8MA&ixlib=rb-4.1.0&q=85";
const MODEL_IMG =
    "https://images.unsplash.com/photo-1740933084056-078fac872bff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaH3Y29uZmVyZW5jZSUyMHJvb218ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";
const PRINCIPLES_IMG =
    "https://images.unsplash.com/photo-1779384570168-cf04dc968a97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHxhcmNoaXRlY3R1cmFsJTIwcm9vZmxpbmV8ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";

const Narrative = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={NARRATIVE_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal className="max-w-3xl">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">The Firm</p>
                <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                    Founded in the Sonoran Desert. Built for the weight of compliance.
                </h2>
                <div className="mt-8 space-y-6 text-base leading-relaxed text-slate-400">
                    <p>
                        Nationwide Strategies LLC has been in business since 2004. Headquartered in
                        Tucson, Arizona, the firm was founded by career receivables industry
                        executives with more than three decades of agency leadership experience. The firm exists to give agency
                        owners, chief executives, and leadership teams something the market rarely
                        offers: consulting from people who have personally run the operations they now
                        advise on.
                    </p>
                    <p>
                        More than two decades of continuous service to the receivables industry
                        stand behind every engagement we accept.
                    </p>
                    <p>
                        The practice is deliberately discreet. We publish no client list, display no
                        logos, and make no claim we cannot verify. References are furnished directly
                        and only with client consent; biographies and credentials are added to this
                        site only once verified.
                    </p>
                    <p>
                        From Arizona, the firm serves collection agencies, debt buyers, and
                        receivables organizations, and accepts selective national engagements
                        where the mandate fits the practice.
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);

const HybridModel = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={MODEL_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">The Hybrid Model</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    A boutique core. Depth on demand.
                </h2>
            </Reveal>
            <div className="mt-14 grid gap-6 lg:grid-cols-3">
                {[
                    {
                        tag: "Core",
                        title: "The Principals",
                        body: "Founding executives with 30-plus years of agency leadership experience lead every engagement personally: scoping, analysis, findings, and consulting.",
                    },
                    {
                        tag: "Network",
                        title: "Senior Subject-Matter Experts",
                        body: "A vetted network of senior specialists in compliance, operations, training, and technology, engaged selectively when a mandate requires depth in a narrow field.",
                    },
                    {
                        tag: "Engagement",
                        title: "Sized to the Mandate",
                        body: "Each engagement team is assembled for the scope at hand and dissolved when the work is done. Clients pay for senior judgment, not standing overhead.",
                    },
                ].map((b, i) => (
                    <Reveal key={b.tag} delay={i * 0.08} className="h-full">
                        <div data-testid={`model-block-${i + 1}`} className="group flex h-full flex-col border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">{b.tag}</span>
                            <h3 className="mt-6 font-serif text-2xl font-medium text-slate-100">{b.title}</h3>
                            <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{b.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const PrinciplesSection = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={PRINCIPLES_IMG} />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
            <Reveal className="lg:col-span-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Guiding Principles</p>
                <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                    The standard we hold ourselves to.
                </h2>
                <p className="mt-6 border-l-2 border-blue-500/60 pl-5 font-serif text-lg italic leading-relaxed text-slate-300">
                    “We make no claim we cannot verify, and we begin with ourselves.”
                </p>
            </Reveal>
            <div className="space-y-4 lg:col-span-8">
                {PRINCIPLES.map((p, i) => (
                    <Reveal key={p.title} delay={i * 0.05}>
                        <div data-testid={`principle-row-${i + 1}`} className="grid gap-2 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-6 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:gap-8 sm:px-6">
                            <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500 sm:col-span-2">
                                P.0{i + 1}
                            </span>
                            <h3 className="font-serif text-xl text-slate-100 sm:col-span-4">{p.title}</h3>
                            <p className="text-sm leading-relaxed text-slate-400 sm:col-span-6">{p.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const LeadershipPlaceholders = () => (
    <section className="border-b border-slate-800/70 bg-[#090d14]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Leadership</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    Principal biographies, published on verification.
                </h2>
                <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
                    In keeping with the firm's standard of discretion, principal biographies,
                    credentials, and professional histories are published here only after independent
                    verification. Until then, the structure below is intentionally reserved.
                </p>
            </Reveal>
            <div className="mt-14 grid gap-6 md:grid-cols-2">
                {[1, 2].map((n) => (
                    <Reveal key={n} delay={n * 0.08} className="h-full">
                        <div data-testid={`principal-placeholder-${n}`} className="flex h-full min-h-[16rem] flex-col justify-between border border-dashed border-amber-500/60 bg-[#0b0f16]/60 p-8">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500">
                                    Principal 0{n}
                                </span>
                                <span className="border border-amber-500/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                                    Pending Verification
                                </span>
                            </div>
                            <p className="mt-10 max-w-sm text-sm leading-relaxed text-slate-500">
                                Biography, command history, credentials, and references, reserved
                                until verified and approved for publication.
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function About() {
    return (
        <>
            <PageMeta
                title="About | Nationwide Strategies LLC"
                description="A discreet, principal-led collection agency consulting firm headquartered in Tucson, Arizona, founded by executives with 30-plus years of industry experience."
            />
            <PageHero
                testid="about-hero"
                kicker="About the Firm"
                lines={["Discretion is not", "a posture. It is", "the practice."]}
                lede="A principal-led boutique headquartered in Tucson, Arizona, advising collection agency and receivables executives across Arizona and, selectively, the nation."
                image={HERO_IMG}
            />
            <Narrative />
            <HybridModel />
            <PrinciplesSection />
            <LeadershipPlaceholders />
            <CTABanner />
        </>
    );
}
